/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  PodFooter,
  PodSection,
  PodSpacer,
  PodSticker,
  PodRow,
  PodTapper,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  BundleDataModel
} from '@thd-olt-component-react/product-pod';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { ProductSeeSimilarItems } from '@thd-olt-component-react/product-pod-group';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { AisleBayPod } from '@thd-olt-component-react/aisle-bay';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';

export const CustomProductPianoPodMobile = ({
  itemId,
  parentId,
  storeId,
  position,
  url,
  itemRecsIndex,
  hasInStoreFilter,
  parent,
  impProductPodData,
  showProjectQuantity,
  hasSameDayFilter,
  isFulfillmentFilterAndMsgEnabled,
  minimumOrderQuantity
}) => {
  const superSkuChangedItemIdsRef = useRef([]);
  const theme = useTheme(CustomProductPianoPodMobile);
  const { hidePickupFulfillment } = theme?.props || {};

  const showFulfillmentPod = (filterToCheck) => {
    return isFulfillmentFilterAndMsgEnabled ? !filterToCheck : true;
  };

  const minOrderQty = minimumOrderQuantity > 0 ? minimumOrderQuantity : undefined;

  return (
    <ProductPod
      analyticsData={{ parent, position }}
      analyticImpressionData={impProductPodData}
      hasSSKU
      itemId={itemId}
      storeId={storeId}
      padding="sui-p-2 sui-rounded-lg"
      render={(pod) => (
        <>
          <a
            className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
            href={url}
            aria-label="Link"
          />
          <div className="sui-relative sui-grid sui-h-full">
            {/* Parent of Col 1 and Col 2 */}
            <PodSection
              display="sui-grid"
              gridColumns="sui-grid-cols-11"
              gap="sui-gap-2"
              noPadding
            >
              {/* Col 1 - ProductImage + Super Sku Mini Swatches */}
              {/* PodMediaContainer */}
              <div
                className="sui-bg-subtle sui-grid sui-col-span-5 sui-grid-rows-[1fr_auto_1fr]
                   sui-h-full sui-rounded-lg"
              >
                {/* PodSticker - Badges */}
                <div className="sui-z-20 sui-flex sui-items-start sui-justify-start">
                  <PodSpacer padding={['LEFT']}>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                  </PodSpacer>
                </div>
                {/* / PodSticker - Badges */}
                {/* ProductImage */}
                <div className="sui-z-20 sui-flex sui-items-center sui-justify-center">
                  <ProductImage
                    itemId={pod.itemId}
                    showSecondaryImage={pod.showSecondaryImage}
                    isBlendedImageWithBackground
                  />
                </div>
                {/* / ProductImage */}
                {/* Super Sku Mini Swatches */}
                <div className="sui-grid sui-w-full">
                  <div className="sui-grid sui-w-full sui-p-1 sui-z-20 sui-content-end">
                    <ProductSample itemId={pod.itemId} />
                    <SuperSkuMiniSwatches
                      storeId={storeId}
                      itemId={pod.itemId}
                      onChange={(clickData) => {
                        if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
                          superSkuChangedItemIdsRef.current = [
                            ...superSkuChangedItemIdsRef.current,
                            clickData.itemId
                          ];
                        }
                        pod.onChange(clickData);
                      }}
                      onHover={pod.onSSKUHover}
                      componentImpressionData={{
                        name: 'ProductPod',
                        component: 'ProductPod',
                        position: position + 1,
                        type: 'product',
                        anchorSku: parentId
                      }}
                      isImpressionsRequired
                      hideComponentWhenEmpty
                      removeStaticHeight
                      swatchMediaSize="6"
                    />
                    <ProductServiceAddOns itemId={pod.itemId} />
                  </div>
                </div>
                {/* / Super Sku Mini Swatches */}
              </div>
              {/* / Col 1 - Pod Media Container */}
              {/* Col 2 - ProductHeader + ProductRating + ProductIdentifier + rest of pod */}
              <PodSection
                display="sui-grid sui-col-span-6"
                gap="sui-gap-0"
                order="sui-order-2 md:sui-order-3"
                alignTop
                noPadding
                columnAlign
              >
                <PodTapper fitWidth zIndex>
                  <PodSpacer>
                    <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
                    <ProductHeader
                      brand="inline"
                      brandLineHeight="tight"
                      brandFontSize="sm"
                      productNameLineHeight="tight"
                      itemId={pod.itemId}
                      brandMaxLine={1}
                      titleMaxLine={3}
                      disableShopThisCollection
                    />
                  </PodSpacer>
                </PodTapper>
                <PodSection noPadding columnAlign padding="sui-py-1">
                  <PodTapper fitWidth zIndex>
                    <ProductIdentifier itemId={pod.itemId} type="model" />
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['TOP']}
                      disable={pod.productSubType === 'HDQC'}
                    >
                      <Price
                        clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                        disableOuterSpacing
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        showProjectPrice
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        hideForProductTypes={['HDQC']}
                        stackWasPrice
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer padding={['TOP']}>
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType={parent.toUpperCase()}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign padding="sui-py-1">
                  <PodTapper fitWidth zIndex>
                    <ProductRating itemId={pod.itemId} />
                  </PodTapper>
                </PodSection>
                <PodFooter noPadding margin="sui-mt-auto">
                  <PodSection display="sui-grid" columnAlign noPadding alignEnd>
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      spanPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <PodSpacer
                        padding={['B2']}
                        disable={pod.productSubType === 'HDQC' || pod.productSubType === 'SFI'}
                        minHeight="60px"
                      >
                        { !hidePickupFulfillment && showFulfillmentPod(hasSameDayFilter) && (
                          <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} isNarrowPod />
                        )}
                        { showFulfillmentPod(hasInStoreFilter) && (
                          <FulfillmentPodShipping
                            itemId={pod.itemId}
                            storeId={storeId}
                            hasSameDayFilter={hasSameDayFilter}
                            isNarrowPod
                          />
                        )}
                      </PodSpacer>
                      <PodSpacer>
                        <AisleBayPod
                          itemId={pod.itemId}
                          clsRemediation={{ placeholders: true, preservePlaceholders: false }}
                          storeId={storeId}
                          padding="sui-mb-2"
                        />
                      </PodSpacer>
                    </PodTapper>
                  </PodSection>
                  <PodSection display="sui-grid" gap="sui-gap-0" columnAlign noPadding alignEnd>
                    <PodTapper zIndex>
                      <PodRow noPadding>
                        <div className="sui-flex-1">
                          <ProductATC
                            hasInStoreFilter={hasInStoreFilter}
                            showProjectQuantity={showProjectQuantity}
                            storeId={storeId}
                            itemId={pod.itemId}
                            checkGeneric
                            outline
                            variant="primary"
                            quantity={minOrderQty}
                          />
                          <ProductSeeSimilarItems
                            itemId={pod.itemId}
                            position={itemRecsIndex}
                            productPodRef={pod.ref}
                            storeId={storeId}
                          />
                          <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                            <NonBuyableQuoteCenter
                              itemId={pod.itemId}
                              checkForProductSubType={['HDQC']}
                            />
                          </PodSpacer>
                        </div>
                      </PodRow>
                    </PodTapper>
                  </PodSection>
                </PodFooter>
              </PodSection>
            </PodSection>
          </div>
        </>
      )}
    />
  );
};

CustomProductPianoPodMobile.displayName = 'CustomProductPianoPodMobile';

CustomProductPianoPodMobile.themeProps = {
  hideAddToListIcon: PropTypes.bool,
  hidePickupFulfillment: PropTypes.bool
};

CustomProductPianoPodMobile.defaultThemeProps = {
  hideAddToListIcon: false,
  hidePickupFulfillment: false
};

CustomProductPianoPodMobile.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  itemRecsIndex: PropTypes.number.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  showProjectQuantity: PropTypes.bool,
  hasSameDayFilter: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
  minimumOrderQuantity: PropTypes.number
};

CustomProductPianoPodMobile.defaultProps = {
  impProductPodData: null,
  parent: 'plp',
  showProjectQuantity: false,
  hasSameDayFilter: false,
  isFulfillmentFilterAndMsgEnabled: false,
  minimumOrderQuantity: 0,
  parentId: null
};

CustomProductPianoPodMobile.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  AddToList,
  Promotion,
  Price,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductSeeSimilarItems,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  NonBuyableQuoteCenter,
  PriceClearanceDataModel,
  BundleDataModel
);
